import React from 'react';
import type { ClassMemberRow } from './classMembersStorage';
import { classMemberNameMatches, displayClassMemberName } from './classMemberNameDisplay';
import { peekSealedTagLabel } from './memoTagLabelCache';

const TAG_RE = /#[^\s#]+/g;

/** 등록 멤버 태그 접두사: `#m:{memberId}` / 제외 `#m:{memberId}-` */
export const MEMO_TAG_MEMBER_PREFIX = 'm:';
/** 미등록(자유) 태그 봉인 접두사: `#e:{sealed}` */
export const MEMO_TAG_SEALED_PREFIX = 'e:';

/** ProgramProgressTable 의 parseMemoFieldData 와 동일(수업기록 JSON text/note) */
export function parseMemoFieldStored(raw: string): { text: string; note: string } {
  const initial = (raw ?? '').trim();
  if (!initial) return { text: '', note: '' };

  let text = initial;
  let note = '';

  for (let i = 0; i < 5; i += 1) {
    const candidate = text.trim();
    if (!candidate.startsWith('{')) break;
    try {
      const parsed = JSON.parse(candidate) as { text?: unknown; note?: unknown };
      const nextText = typeof parsed?.text === 'string' ? parsed.text : '';
      const nextNote = typeof parsed?.note === 'string' ? parsed.note : '';
      if (nextNote && !note) note = nextNote;
      text = nextText;
    } catch {
      break;
    }
  }

  return { text: text.trim(), note: note.trim() };
}

export type ParsedMemoTag =
  | { kind: 'all'; raw: string }
  | { kind: 'exclude'; raw: string; keyword: string; memberId?: string }
  | { kind: 'include'; raw: string; keyword: string; memberId?: string; sealed?: boolean };

/** false: #전체·#이름- 를 집합 규칙으로 쓰지 않음(학급 전체 문맥·그룹 미지정 레거시 기록) */
export type ClassRecordMemoTagRuleOptions = {
  applyBulkMemberTagRules?: boolean;
  /** `#e:…` 토큰 → 평문 라벨 (세션 메모리; DB/캐시에는 봉인본만) */
  sealedLabelByToken?: Record<string, string>;
};

export function isMemberTagBody(body: string): boolean {
  const b = (body ?? '').trim();
  if (!b.startsWith(MEMO_TAG_MEMBER_PREFIX)) return false;
  const rest = b.slice(MEMO_TAG_MEMBER_PREFIX.length);
  if (!rest) return false;
  if (rest.endsWith('-') && rest.length > 1) return rest.slice(0, -1).length > 0;
  return true;
}

export function isSealedTagBody(body: string): boolean {
  const b = (body ?? '').trim();
  return b.startsWith(MEMO_TAG_SEALED_PREFIX) && b.length > MEMO_TAG_SEALED_PREFIX.length;
}

export function parseMemberIdFromTagBody(body: string): { memberId: string; exclude: boolean } | null {
  const b = (body ?? '').trim();
  if (!b.startsWith(MEMO_TAG_MEMBER_PREFIX)) return null;
  let rest = b.slice(MEMO_TAG_MEMBER_PREFIX.length);
  let exclude = false;
  if (rest.endsWith('-') && rest.length > 1) {
    exclude = true;
    rest = rest.slice(0, -1);
  }
  if (!rest) return null;
  return { memberId: rest, exclude };
}

export function buildMemberTagBody(memberId: string, exclude = false): string {
  const id = (memberId ?? '').trim();
  if (!id) return '';
  return exclude ? `${MEMO_TAG_MEMBER_PREFIX}${id}-` : `${MEMO_TAG_MEMBER_PREFIX}${id}`;
}

export function buildMemberTagRaw(memberId: string, exclude = false): string {
  const body = buildMemberTagBody(memberId, exclude);
  return body ? `#${body}` : '';
}

export function buildSealedTagBody(sealedPayload: string): string {
  const p = (sealedPayload ?? '').trim();
  if (!p) return '';
  if (p.startsWith(MEMO_TAG_SEALED_PREFIX)) return p;
  return `${MEMO_TAG_SEALED_PREFIX}${p}`;
}

export function buildSealedTagRaw(sealedPayload: string): string {
  const body = buildSealedTagBody(sealedPayload);
  return body ? `#${body}` : '';
}

/** note 문자열에서 `#e:…` 토큰(전체 raw) 고유 목록 */
export function collectSealedTagTokens(text: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const re = new RegExp(TAG_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text ?? '')) !== null) {
    const raw = m[0];
    const body = raw.slice(1);
    if (!isSealedTagBody(body)) continue;
    if (seen.has(raw)) continue;
    seen.add(raw);
    out.push(raw);
  }
  return out;
}

/** 멤버 목록 순서대로 첫 일치 (이름에 keyword가 포함되는 경우) — 필터 등 */
export function resolveMemberByKeyword(members: ClassMemberRow[], keyword: string): ClassMemberRow | null {
  const k = (keyword ?? '').trim();
  if (!k) return null;
  const byId = parseMemberIdFromTagBody(k);
  if (byId) return members.find((m) => m.id === byId.memberId) ?? null;
  return members.find((m) => classMemberNameMatches(m, k, 'includes')) ?? null;
}

/**
 * 칩·툴팁 표시용: `#m:id` 우선, 그다음 includes 로 1명만 걸릴 때만 매칭.
 * (학생01·학생02 모두에 걸리는 #학생0 은 애매하므로 칩/라벨에 쓰지 않음)
 */
export function resolveMemberTagUnique(members: ClassMemberRow[], keyword: string): ClassMemberRow | null {
  const k = (keyword ?? '').trim();
  if (!k) return null;
  const byId = parseMemberIdFromTagBody(k);
  if (byId) return members.find((m) => m.id === byId.memberId) ?? null;
  if (isSealedTagBody(k)) return null;
  const hits = members.filter((m) => classMemberNameMatches(m, k, 'includes'));
  if (hits.length === 1) return hits[0];
  return members.find((m) => classMemberNameMatches(m, k, 'exact')) ?? null;
}

/**
 * 입력된 태그 본문( # 제외 )을 저장용 본문으로 정규화(동기: 멤버 매칭만).
 * 미등록이면 null — 호출측에서 seal RPC 후 `#e:…` 본문을 넣는다.
 */
export function normalizeTagBodyToMemberOrNull(typedBody: string, members: ClassMemberRow[]): string | null {
  const body = (typedBody ?? '').trim();
  if (!body) return null;
  if (body === '전체') return '전체';
  if (isMemberTagBody(body) || isSealedTagBody(body)) return body;

  let exclude = false;
  let keyword = body;
  if (body.endsWith('-') && body.length > 1) {
    exclude = true;
    keyword = body.slice(0, -1);
  }
  const hit = resolveMemberTagUnique(members, keyword);
  if (hit) return buildMemberTagBody(hit.id, exclude);
  return null;
}

/**
 * 닫힌 태그(뒤에 일반 공백 U+0020 한 칸, 또는 blur 시 맨 끝)만 칩으로 올릴 때 내용이 유효한지.
 */
export function isChippableClosedTag(
  raw: string,
  members: ClassMemberRow[],
  options?: ClassRecordMemoTagRuleOptions
): boolean {
  if (!/^#[^\s#]+$/.test(raw)) return false;
  const body = raw.slice(1);
  if (!body) return false;
  const bulk = options?.applyBulkMemberTagRules !== false;
  if (body === '전체') return bulk;
  if (isMemberTagBody(body)) {
    const parsed = parseMemberIdFromTagBody(body);
    if (!parsed) return false;
    if (parsed.exclude && !bulk) return false;
    return true;
  }
  if (isSealedTagBody(body)) return true;
  if (body.endsWith('-') && body.length > 1) {
    return bulk && body.slice(0, -1).length >= 1;
  }
  const uniqueMember = resolveMemberTagUnique(members, body);
  return uniqueMember != null || body.length > 0;
}

/**
 * 직렬화 note에서 해당 매치가 칩으로 렌더될 자격인지.
 * `#m:`/`#e:` 안정 ID 태그는 닫는 공백이 사라져도 칩을 유지해 raw 노출을 막는다.
 */
export function isTagMatchRenderedAsChip(
  raw: string,
  nextCh: string,
  members: ClassMemberRow[],
  options?: ClassRecordMemoTagRuleOptions & { finalizeOpenTags?: boolean }
): boolean {
  if (!isChippableClosedTag(raw, members, options)) return false;
  const body = raw.slice(1);
  if (isMemberTagBody(body) || isSealedTagBody(body)) return true;
  const explicitCloseBySpace = nextCh === ' ';
  const atEof = nextCh === '';
  return explicitCloseBySpace || (Boolean(options?.finalizeOpenTags) && atEof);
}

export function countChippableTagsInNote(
  note: string,
  members: ClassMemberRow[],
  options: { finalizeOpenTags: boolean } & ClassRecordMemoTagRuleOptions
): number {
  const re = new RegExp(TAG_RE.source, 'g');
  let c = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(note)) !== null) {
    const raw = m[0];
    const end = m.index + raw.length;
    const nextCh = end >= note.length ? '' : note[end]!;
    if (isTagMatchRenderedAsChip(raw, nextCh, members, options)) c += 1;
  }
  return c;
}

/** 닫힌 `#태그 ` 패턴의 태그 본문 목록(순서 유지) */
export function listClosedChippableTagBodiesInNote(
  note: string,
  members: ClassMemberRow[],
  options?: ClassRecordMemoTagRuleOptions
): string[] {
  const bodies: string[] = [];
  const re = /#([^\s#]+) /g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(note)) !== null) {
    const raw = `#${m[1]}`;
    if (isChippableClosedTag(raw, members, options)) bodies.push(m[1]);
  }
  return bodies;
}

/**
 * 평가기록 메모 기본값: 멤버마다 닫힌 `#m:{id} ` 태그를 한 줄에 하나씩.
 */
/** 평가기록 메모에 멤버 태그가 아닌 본문이 남아 있는지 */
export function evalSerializedNoteHasNonTagText(serialized: string): boolean {
  const stripped = (serialized ?? '')
    .replace(/#[^\s#]+/g, '')
    .replace(/\u200b/g, '')
    .replace(/\s+/g, '')
    .trim();
  return stripped.length > 0;
}

export function buildDefaultEvalMemberTagsNote(members: ClassMemberRow[]): string {
  const lines: string[] = [];
  for (const m of members) {
    const id = (m.id ?? '').trim();
    if (!id) continue;
    lines.push(`${buildMemberTagRaw(id)} `);
  }
  return lines.join('\n');
}

function sealedDisplayLabel(raw: string, options?: ClassRecordMemoTagRuleOptions): string {
  const map = options?.sealedLabelByToken;
  if (map && map[raw]) return map[raw];
  const body = raw.startsWith('#') ? raw.slice(1) : raw;
  if (map && map[`#${body}`]) return map[`#${body}`];
  const cached = peekSealedTagLabel(raw) ?? peekSealedTagLabel(`#${body}`);
  if (cached) return cached;
  return '비공개 태그';
}

export function formatClassRecordTagDisplay(
  raw: string,
  members: ClassMemberRow[],
  options?: ClassRecordMemoTagRuleOptions
): string {
  const token = (raw ?? '').trim();
  if (!token.startsWith('#')) return token;
  const body = token.slice(1);
  const bulk = options?.applyBulkMemberTagRules !== false;
  if (body === '전체') return bulk ? '전체' : token;
  if (isSealedTagBody(body)) return sealedDisplayLabel(token, options);
  const memberParsed = parseMemberIdFromTagBody(body);
  if (memberParsed) {
    const hit = members.find((m) => m.id === memberParsed.memberId);
    if (memberParsed.exclude) {
      if (!bulk) return token;
      return hit ? `${displayClassMemberName(hit)} 제외` : '멤버 제외';
    }
    return hit ? displayClassMemberName(hit) : '멤버';
  }
  if (body.endsWith('-') && body.length > 1) {
    const kw = body.slice(0, -1);
    if (!bulk) return token;
    const hit = resolveMemberTagUnique(members, kw);
    return hit ? `${displayClassMemberName(hit)} 제외` : `${kw} 제외`;
  }
  if (!body) return token;
  const hit = resolveMemberTagUnique(members, body);
  return hit ? displayClassMemberName(hit) : token;
}

export function parseClassRecordMemoTags(note: string): ParsedMemoTag[] {
  const s = note ?? '';
  const out: ParsedMemoTag[] = [];
  let m: RegExpExecArray | null;
  const re = new RegExp(TAG_RE.source, 'g');
  while ((m = re.exec(s)) !== null) {
    const raw = m[0];
    const body = raw.slice(1);
    if (body === '전체') {
      out.push({ kind: 'all', raw });
    } else if (isMemberTagBody(body)) {
      const parsed = parseMemberIdFromTagBody(body);
      if (!parsed) continue;
      if (parsed.exclude) {
        out.push({ kind: 'exclude', raw, keyword: parsed.memberId, memberId: parsed.memberId });
      } else {
        out.push({ kind: 'include', raw, keyword: parsed.memberId, memberId: parsed.memberId });
      }
    } else if (isSealedTagBody(body)) {
      out.push({ kind: 'include', raw, keyword: body, sealed: true });
    } else if (body.endsWith('-') && body.length > 1) {
      out.push({ kind: 'exclude', raw, keyword: body.slice(0, -1) });
    } else if (body.length > 0) {
      out.push({ kind: 'include', raw, keyword: body });
    }
  }
  return out;
}

function keywordMatchesMember(keyword: string, member: ClassMemberRow, asMemberId?: string): boolean {
  if (asMemberId) return member.id === asMemberId;
  if (member.id === keyword) return true;
  return classMemberNameMatches(member, keyword, 'includes');
}

/**
 * 수업기록 메모(note)가 선택한 멤버와 태그 규칙상 연결되는지.
 */
export function memoMatchesMember(
  note: string,
  member: ClassMemberRow,
  _allMembers: ClassMemberRow[],
  options?: ClassRecordMemoTagRuleOptions
): boolean {
  void _allMembers;
  const bulk = options?.applyBulkMemberTagRules !== false;
  const tags = parseClassRecordMemoTags(note);
  let hasAll = false;
  const excludeTags: ParsedMemoTag[] = [];
  const includeTags: ParsedMemoTag[] = [];
  tags.forEach((t) => {
    if (t.kind === 'all') {
      if (bulk) hasAll = true;
    } else if (t.kind === 'exclude') {
      if (bulk) excludeTags.push(t);
    } else if (!t.sealed) {
      includeTags.push(t);
    }
  });

  const excluded = excludeTags.some((t) =>
    t.kind === 'exclude' && keywordMatchesMember(t.keyword, member, t.memberId)
  );
  const includedByTag = includeTags.some((t) =>
    t.kind === 'include' && keywordMatchesMember(t.keyword, member, t.memberId)
  );
  const included = includedByTag || hasAll;
  return included && !excluded;
}

/**
 * 줄바꿈(`\n`) 기준으로 메모를 분리해,
 * 선택 멤버와 태그 규칙이 맞는 "태그 포함 줄"만 다시 합친다.
 */
export function extractMemberMatchedTaggedLines(
  note: string,
  member: ClassMemberRow,
  allMembers: ClassMemberRow[],
  options?: ClassRecordMemoTagRuleOptions
): string {
  const s = note ?? '';
  if (!s.trim()) return '';
  const lines = s.split('\n');
  const kept: string[] = [];
  for (const line of lines) {
    if (!line.trim()) continue;
    if (parseClassRecordMemoTags(line).length === 0) continue;
    if (!memoMatchesMember(line, member, allMembers, options)) continue;
    kept.push(line);
  }
  return kept.join('\n');
}

function tagChip(className: string, key: string, label: string): React.ReactElement {
  return React.createElement('span', { key, className, children: label });
}

/** note 문자열을 그대로 두고 UI용으로 #토큰만 칩으로 갈아끼움 */
export function renderNoteWithMemberTags(
  note: string,
  members: ClassMemberRow[],
  options?: ClassRecordMemoTagRuleOptions
): React.ReactNode {
  const s = note ?? '';
  if (!s) return null;
  const bulk = options?.applyBulkMemberTagRules !== false;
  const parts: React.ReactNode[] = [];
  let last = 0;
  let mi = 0;
  const re = new RegExp(TAG_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) {
      parts.push(s.slice(last, m.index));
    }
    const raw = m[0];
    const body = raw.slice(1);
    const key = `t-${mi++}`;
    if (body === '전체') {
      if (bulk) {
        parts.push(tagChip('cp-memo-member-tag cp-memo-member-tag-all', key, '전체'));
      } else {
        parts.push(tagChip('cp-memo-member-tag', key, raw));
      }
    } else if (isSealedTagBody(body)) {
      parts.push(tagChip('cp-memo-member-tag', key, sealedDisplayLabel(raw, options)));
    } else if (isMemberTagBody(body)) {
      const parsed = parseMemberIdFromTagBody(body);
      if (parsed?.exclude) {
        if (bulk) {
          const hit = members.find((x) => x.id === parsed.memberId);
          parts.push(
            tagChip(
              'cp-memo-member-tag cp-memo-member-tag-exclude',
              key,
              hit ? `${displayClassMemberName(hit)} 제외` : '멤버 제외'
            )
          );
        } else {
          parts.push(tagChip('cp-memo-member-tag', key, raw));
        }
      } else {
        const hit = parsed ? members.find((x) => x.id === parsed.memberId) : null;
        parts.push(tagChip('cp-memo-member-tag', key, hit ? displayClassMemberName(hit) : '멤버'));
      }
    } else if (body.endsWith('-') && body.length > 1) {
      const kw = body.slice(0, -1);
      if (bulk) {
        const hit = resolveMemberTagUnique(members, kw);
        parts.push(
          tagChip(
            'cp-memo-member-tag cp-memo-member-tag-exclude',
            key,
            hit ? `${displayClassMemberName(hit)} 제외` : `${kw} 제외`
          )
        );
      } else {
        parts.push(tagChip('cp-memo-member-tag', key, raw));
      }
    } else if (body.length > 0) {
      const hit = resolveMemberTagUnique(members, body);
      const label = hit ? displayClassMemberName(hit) : raw;
      parts.push(tagChip('cp-memo-member-tag', key, label));
    } else {
      parts.push(raw);
    }
    last = m.index + raw.length;
  }
  if (last < s.length) {
    parts.push(s.slice(last));
  }
  return React.createElement(React.Fragment, null, ...parts);
}
